import { useCallback } from 'react';
import type { VParcelaTecnica } from '@aosenuma/lib-types';
import { getSupabase, isSupabaseConfigured } from '../client';
import { fetchAllPaginated } from '../paginate';
import { useCachedFetch } from './useCachedFetch';

/**
 * Inventario del panel técnico: vista `v_parcelas_tecnicas` (PIPELINE.md 4.3).
 * Sin Supabase configurado devuelve `data: []`, `loading: false` (no lanza).
 *
 * Cache: stale-while-revalidate por sessionStorage (ttl 60s). Importante
 * porque la query es paginated 2374 rows = ~1.5s en cada mount sin cache.
 * Al volver al tab desde otra pestaña, el render es instantáneo desde el
 * cache mientras se revalida en background.
 */
export function useParcelasTecnicas(): {
  data: VParcelaTecnica[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
} {
  const fetcher = useCallback(async (): Promise<VParcelaTecnica[]> => {
    if (!isSupabaseConfigured()) return [];
    const sb = getSupabase();
    // Supabase aplica `pgrst.db_max_rows = 1000` server-side: el `.range(0, N)`
    // del cliente es ignorado si N > 999. Confirmado via curl con service role
    // (response: content-range 0-999/2374 incluso pidiendo Range: 0-9999).
    // Solucion: paginar lotes de 1000 hasta agotar.
    return fetchAllPaginated<VParcelaTecnica>(
      (from, to) =>
        sb.from('v_parcelas_tecnicas').select('*')
          .order('parcela_id', { ascending: true })
          .range(from, to),
      1000,
      20_000,
    );
  }, []);

  const { data, loading, error, refetch } = useCachedFetch<VParcelaTecnica[]>(
    'parcelas_tecnicas',
    fetcher,
    { ttlMs: 60_000 },
  );

  return { data: data ?? [], loading, error, refetch };
}
